<template>
	<div
		class="app-select"
		:class="{
			'app-select--open': isOpen,
			'app-select--disabled': disabled,
			'app-select--multiple': multiple,
			'app-select--focused': isFocused,
			'app-select--has-groups': hasGroups,
			'app-select--searchable': isInlineSearch,
			'app-select--clearable': clearable
		}"
		v-click-outside="closeDropdown"
	>
		<div
			ref="triggerRef"
			:class="{
				'app-select__trigger': true,
				'app-select__trigger--editable': isInlineSearch && isOpen
			}"
			:style="customTriggerStyle"
			:tabindex="disabled ? -1 : 0"
			@click="handleTriggerClick"
			@keydown.enter.prevent="handleTriggerEnter"
			@keydown.esc="closeDropdown"
			@keydown.down.prevent="handleTriggerKeyDown"
			@keydown.up.prevent="handleTriggerKeyDown"
			@focus="isFocused = true"
			@blur="isFocused = false"
		>
			<label
				v-if="floatingLabel && resolvedPlaceholder"
				:class="{
					'app-select__floating-label': true,
					active: isLabelActive
				}"
			>
				{{ resolvedPlaceholder }}
				<span
					v-if="required"
					class="app-select__floating-label-required"
				>*</span>
			</label>
			<div
				:class="{
					'app-select__values': true,
					'app-select__values--hidden': isSearchFieldActive
				}"
			>
				<template v-if="isStandardMultiple">
					<span
						v-if="selectedValues.length"
						class="app-select__single-value"
					>
						{{ selectedCountLabel }}
					</span>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ triggerPlaceholder }}
					</span>
				</template>

				<template v-else-if="multiple && dropdownLabel==undefined">
					<template v-if="selectedDisplayItems.length">
						<span class="app-select__first-value">
							{{ selectedDisplayItems[0].label }}
						</span>
						<span
							v-if="selectedDisplayItems.length > 1"
							class="app-select__additional-count"
						>
							+{{ selectedDisplayItems.length - 1 }}
						</span>
					</template>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ triggerPlaceholder }}
					</span>
				</template>

				<template v-else-if="dropdownLabel">
					<template v-if="selectedDisplayItems.length">
						<span class="app-select__first-value">
							{{ dropdownLabel }}
						</span>
					</template>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ triggerPlaceholder }}
					</span>
				</template>

				<template v-else>
					<span
						v-if="selectedValue != null"
						class="app-select__single-value"
					>
						{{ getDisplayValue(selectedValue) }}
					</span>
					<span
						v-else
						class="app-select__placeholder"
					>
						{{ triggerPlaceholder }}
					</span>
				</template>
			</div>
			<div
				v-if="isInlineSearch && isOpen"
				:class="{
					'app-select__edit-container': true,
					'app-select__edit-container--hidden': !isSearchFieldActive
				}"
				:style="editContainerStyle"
				@click.stop
			>
				<input
					ref="editInputRef"
					v-model="searchQuery"
					class="app-select__edit-input"
						:placeholder="resolvedSearchPlaceholder"
					@focus="isSearchActive = true"
					@keydown.enter.prevent.stop="handleSearchEnter"
					@keydown.down.prevent.stop="handleDropdownKeyDown"
					@keydown.up.prevent.stop="handleDropdownKeyDown"
				/>
			</div>

			<div class="app-select__actions" @click.stop>
				<app-icon
					v-if="showClearIcon"
					class="app-select__clear-button"
					name="arrow-left"
					width="16px"
					color="#979797"
					@mousedown.prevent
					@click.stop="handleClearClick"
				/>

				<app-icon
					v-if="showSearchToggle"
					class="app-select__search-toggle"
					name="search-magnify"
					width="16px"
					:color="isSearchActive ? '#3F884F' : '#979797'"
					:hover-color="isSearchActive ? '#3F884F' : '#499259'"
					cursor="pointer"
					@mousedown.prevent
					@click.stop="toggleSearch"
				/>

				<slot name="append-icon" :close="closeDropdown">
					<app-icon
						class="app-select__arrow"
						:name="disabled && disabledIcon ? disabledIcon : 'chevron-down'"
						width="16px"
						color="#979797"
						cursor="pointer"
						@click.stop="toggleDropdown"
					/>
				</slot>
			</div>
		</div>

		<Teleport to="body">
			<transition name="select-dropdown">
				<div
					v-if="isOpen"
					:class="[
						'app-select__dropdown',
						dropdownClass,
						{
							'app-select__dropdown--single': !multiple,
							'app-select__dropdown--default': hasDefaultDropdown
						}
					]"
					:style="dropdownStyles"
					ref="dropdownRef"
					@keydown.enter.prevent="handleDropdownEnter"
					@keydown.esc="closeDropdown"
					@keydown.down.prevent="handleDropdownKeyDown"
					@keydown.up.prevent="handleDropdownKeyDown"
				>
					<app-icon
						v-if="showDropdownClose"
						class="app-select__dropdown-close"
						name="close"
						:width="dropdownCloseWidth"
						color="#979797"
						hover-color="#3F884F"
						cursor="pointer"
						@click.stop="closeDropdown"
					/>
					<div class="app-select__dropdown-header" :style="customSearchStyle">
						<div
							v-if="searchable && !isInlineSearch"
							class="app-select__search"
						>
							<app-input
								ref="$searchInputRef"
								v-model="searchQuery"
								class="app-select__search-input"
								:placeholder="resolvedSearchPlaceholder"
								:show-search-button="showSearchButton"
								@click.stop
							/>
						</div>
						<div
							v-if="multiple"
							class="app-select__controls"
							@click.stop
							@mousedown.prevent
						>
							<div class="app-select__controls-left">
								<app-checkbox
									:value="showOnlySelected"
									@on-input="toggleShowOnlySelected"
									@click.stop
								/>
								<span class="app-select__controls-text">{{ $t("selector_show_selected") }}</span>
							</div>
							<div class="app-select__controls-right">
								<button
									class="app-select__reset-button"
									@click.stop="resetSelection"
									:disabled="!selectedValues.length"
								>
									{{ $t("selector_reset") }}
								</button>
							</div>
						</div>
					</div>
					<div v-if="showDivider" class="app-select__divider" />
					<div class="app-select__options-container">
						<slot
							name="dropdown"
							:items="filteredItemsWithSelected"
							:selected="selectedValues"
							:select="handleSelect"
							:getGroupState="getGroupState"
							:close="closeDropdown"
							:search="searchQuery"
						>
							<div class="app-select__options">
								<template v-if="multiple && showSelectAll && selectableItems.length">
									<div class="app-select__option app-select__option--select-all">
										<div class="app-select__option-label" @click.stop>
											<app-checkbox
												:value="isAllSelected"
												@on-input="toggleAll"
											/>
											<span
												class="app-select__group-title"
												:style="{ fontWeight: '400' }"
											>
												{{ $t("all_users") }}
											</span>
										</div>
									</div>
								</template>
								<div
									v-for="item in visiblePinnedItems"
									:key="`pinned-${String(item.id)}`"
									:class="{
										'app-select__option': true,
										'app-select__option--pinned': true,
										'app-select__option--selected': isSelected(item.id),
										'app-select__option--disabled': item.disabled
									}"
									@click="handleItemClick(item)"
								>
									<template v-if="multiple">
										<label class="app-select__option-label" @click.stop @mousedown.prevent>
											<app-checkbox
												:value="isSelected(item.id)"
												:disabled="item.disabled"
												@on-change="handleSelect(item.id, $event)"
											/>
											<span class="app-select__option-content">
												<span class="app-select__option-name">{{ item.label }}</span>
												<app-tooltip
													v-if="getDescription(item)"
													class="app-select__option-tooltip"
													position="bottom"
													text-align="left"
													follow-cursor
													:offset="24"
													:max-width="props.descriptionTooltipMaxWidth"
													:text="getDescriptionTooltip(item)"
												>
													<template #activator>
														<span class="app-select__option-description">{{ getDescription(item) }}</span>
													</template>
												</app-tooltip>
											</span>
										</label>
									</template>
									<template v-else>
										<div class="app-select__option-content">
											<span class="app-select__option-name">{{ item.label }}</span>
											<app-tooltip
												v-if="getDescription(item)"
												class="app-select__option-tooltip"
												position="bottom"
												text-align="left"
												follow-cursor
												:offset="24"
												:max-width="props.descriptionTooltipMaxWidth"
												:text="getDescriptionTooltip(item)"
											>
												<template #activator>
													<span class="app-select__option-description">{{ getDescription(item) }}</span>
												</template>
											</app-tooltip>
										</div>
									</template>
								</div>

								<template
									v-for="item in filteredItemsWithSelected"
									:key="String(item.id)"
								>
									<div
										v-if="item.type === 'group'"
										class="app-select__group"
									>
										<div
											:class="{
												'app-select__group-header': true,
												'app-select__group-header--selectable': isGroupSelectable(item),
												'app-select__group-header--static': !isGroupSelectable(item),
												'app-select__group-header--disabled': item.disabled,
												'app-select__group-header--focused': isGroupSelectable(item) && focusedValue === item.id
											}"
											@click.stop="toggleGroupSelection(item)"
											@mouseenter="handleGroupHeaderEnter(item)"
											@mousedown.prevent
										>
											<label
												v-if="isGroupSelectable(item)"
												class="app-select__group-checkbox"
												@click.stop
											>
												<app-checkbox
													:value="getOwnGroupState(item).isSelected"
													:disabled="item.disabled"
													@on-input="toggleGroupSelection(item)"
												/>
											</label>

											<span class="app-select__group-label">
												<span
													class="app-select__group-title"
													v-html="highlightSearchText(searchQuery, item.label)"
												></span>
												<span class="app-select__group-count">{{ findOriginalGroup(item.id)?.children.length || item.children.length }}</span>
											</span>
										</div>
										<div v-if="item.showChildren" class="app-select__group-children">
											<div
												v-for="child in item.children"
												:key="String(child.id)"
												:class="{
													'app-select__option': true,
													'app-select__option--child': true,
													'app-select__option--selected': isSelected(child.id),
													'app-select__option--disabled': child.disabled,
													'app-select__option--focused': focusedValue === child.id,
													'app-select__option--with-append': !!$slots['option-append']
												}"
												@click="handleItemClick(child)"
												@mouseenter="focusedValue = child.id; hoveredOptionId = child.id"
												@mouseleave="hoveredOptionId = null"
											>
												<template v-if="multiple">
													<label class="app-select__option-label" @click.stop @mousedown.prevent>
														<app-checkbox
															:value="isSelected(child.id)"
															:disabled="child.disabled"
															@on-change="handleSelect(child.id, $event)"
														/>
														<span class="app-select__option-content">
															<span
																class="app-select__option-name"
																v-html="highlightSearchText(searchQuery, child.label)"
															></span>
															<app-tooltip
																v-if="getDescription(child)"
																class="app-select__option-tooltip"
																position="bottom"
																text-align="left"
																follow-cursor
																:offset="24"
																:max-width="props.descriptionTooltipMaxWidth"
																:text="getDescriptionTooltip(child)"
															>
																<template #activator>
																	<span class="app-select__option-description">{{ getDescription(child) }}</span>
																</template>
															</app-tooltip>
														</span>
													</label>
												</template>
												<template v-else>
													<div class="app-select__option-content">
														<span
															class="app-select__option-name"
															v-html="highlightSearchText(searchQuery, child.label)"
														></span>
														<app-tooltip
															v-if="getDescription(child)"
															class="app-select__option-tooltip"
															position="bottom"
															text-align="left"
															follow-cursor
															:offset="24"
															:max-width="props.descriptionTooltipMaxWidth"
															:text="getDescriptionTooltip(child)"
														>
															<template #activator>
																<span class="app-select__option-description">{{ getDescription(child) }}</span>
															</template>
														</app-tooltip>
													</div>
												</template>
												<span
													v-if="$slots['option-append']"
													class="app-select__option-append"
												>
													<slot
														name="option-append"
														:item="child"
														:hovered="hoveredOptionId === child.id"
													/>
												</span>
											</div>
										</div>
									</div>
									<div
										v-else
										:class="{
											'app-select__option': true,
											'app-select__option--selected': isSelected(item.id),
											'app-select__option--disabled': item.disabled,
											'app-select__option--focused': focusedValue === item.id,
											'app-select__option--with-append': !!$slots['option-append']
										}"
										@click="handleItemClick(item)"
										@mouseenter="focusedValue = item.id; hoveredOptionId = item.id"
										@mouseleave="hoveredOptionId = null"
									>
										<template v-if="multiple">
											<label class="app-select__option-label" @click.stop @mousedown.prevent>
												<app-checkbox
													:value="isSelected(item.id)"
													:disabled="item.disabled"
													@on-change="handleSelect(item.id, $event)"
												/>
												<span class="app-select__option-content">
													<span
														class="app-select__option-name"
														v-html="highlightSearchText(searchQuery, item.label)"
													></span>
													<app-tooltip
														v-if="getDescription(item)"
														class="app-select__option-tooltip"
														position="bottom"
														text-align="left"
														follow-cursor
														:offset="24"
														:max-width="props.descriptionTooltipMaxWidth"
														:text="getDescriptionTooltip(item)"
													>
														<template #activator>
															<span class="app-select__option-description">{{ getDescription(item) }}</span>
														</template>
													</app-tooltip>
												</span>
											</label>
										</template>
										<template v-else>
											<div class="app-select__option-content">
												<span
													class="app-select__option-name"
													v-html="highlightSearchText(searchQuery, item.label)"
												></span>
												<app-tooltip
													v-if="getDescription(item)"
													class="app-select__option-tooltip"
													position="bottom"
													text-align="left"
													follow-cursor
													:offset="24"
													:max-width="props.descriptionTooltipMaxWidth"
													:text="getDescriptionTooltip(item)"
												>
													<template #activator>
														<span class="app-select__option-description">{{ getDescription(item) }}</span>
													</template>
												</app-tooltip>
											</div>
										</template>
										<span
											v-if="$slots['option-append']"
											class="app-select__option-append"
										>
											<slot
												name="option-append"
												:item="item"
												:hovered="hoveredOptionId === item.id"
											/>
										</span>
									</div>
								</template>

								<div
									v-if="!filteredItemsWithSelected.length && !visiblePinnedItems.length"
									class="app-select__empty"
								>
									{{
										isSelectedOnlyMode
											? $t("selected_users_not_found")
											: searchQuery
												? searchEmptyText || $t("matching_users_not_found")
												: $t("no_data")
									}}
								</div>
							</div>
						</slot>
					</div>
				</div>
			</transition>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	nextTick,
	onUnmounted,
	useSlots
} from 'vue'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";
import { i18n, tp } from "@/i18n";

export interface SelectItem {
	id: string | number
	label: string
	type?: 'item' | 'group'
	disabled?: boolean
	[key: string]: any
}

export interface SelectGroup extends SelectItem {
	type: 'group'
	children: SelectItem[]
	selectable?: boolean
}

export type SelectOption = SelectItem | SelectGroup
export type SelectModelValue = string | number | (string | number)[] | null

type Props = {
	modelValue?: SelectModelValue
	items?: SelectOption[]
	// Строки, закреплённые над основным списком (например текущий пользователь)
	pinnedItems?: SelectOption[]
	multiple?: boolean
	dropdownLabel?: string
	placeholder?: string
	disabled?: boolean
	showSelectAll?: boolean
	valueKey?: string
	labelKey?: string
	displayKey?: string
	descriptionKey?: string
	// Максимальная ширина подсказки с описанием варианта; 'none' — одна строка без ограничения
	descriptionTooltipMaxWidth?: string
	// Ключ i18n со склонениями для сводки мультиселекта, например job_workers_count
	selectedCountKey?: string
	searchable?: boolean
	searchEmptyText?: string
	showSearchButton?: boolean
	searchPlaceholder?: string
	closeOnSelect?: boolean
	clearable?: boolean,
	minDropdownWidth?: string | number
	dropdownHeight?: string | number
	maxDropdownHeight?: string | number
	customTriggerStyle?: any;
	customSearchStyle?: any;
	dropdownClass?: string;
	ownerChoise?: boolean
	showDropdownClose?: boolean
	showDivider?: boolean
	disabledIcon?: string
	dropdownCloseWidth?: string
	floatingLabel?: boolean
	required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: null,
	items: () => [],
	pinnedItems: () => [],
	multiple: false,
	placeholder: '',
	disabled: false,
	showSelectAll: false,
	valueKey: 'id',
	labelKey: 'label',
	displayKey: undefined,
	descriptionKey: undefined,
	descriptionTooltipMaxWidth: 'none',
	selectedCountKey: undefined,
	searchable: false,
	searchEmptyText: undefined,
	showSearchButton: true,
	searchPlaceholder: '',
	closeOnSelect: true,
	clearable: false,
	showDropdownClose: false,
	showDivider: false,
	disabledIcon: '',
	dropdownCloseWidth: '20px',
	dropdownHeight: undefined,
	maxDropdownHeight: 400,
	minTriggerHeight: 32,
	floatingLabel: false,
	required: false
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: SelectModelValue): void
	(e: 'select', value: SelectModelValue): void
	(e: 'open'): void
	(e: 'close'): void
	(e: 'search', value: string): void
}>()

const isOpen = ref(false)
const isFocused = ref(false)
const isSearchActive = ref(false)
const focusedValue = ref<string | number | null>(null)

const hoveredOptionId = ref<string | number | null>(null)
const searchQuery = ref('')
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const $searchInputRef = ref<typeof AppInput | null>(null)
const editInputRef = ref<HTMLInputElement | null>(null)
const showOnlySelected = ref(false)
const dropdownStyles = ref({})

const slots = useSlots()

// Проверка наличия групп
const hasGroups = computed(() => {
	return props.items.some(item => item.type === 'group')
})

// Дропдаун рисует наш список, а не кастомное содержимое через слот
const hasDefaultDropdown = computed(() => !slots.dropdown)

// «Стандартный» мультиселект: без кастомного содержимого дропдауна и без статичной подписи триггера
const isStandardMultiple = computed(() =>
	props.multiple && hasDefaultDropdown.value && !props.dropdownLabel
)

const isInlineSearch = computed(() =>
	props.searchable && !props.ownerChoise && (!props.multiple || isStandardMultiple.value)
)

// Поисковый инпут перекрывает выбранные значения в поле
const isSearchFieldActive = computed(() => {
	if (!isInlineSearch.value || !isOpen.value) {
		return false
	}

	if (!isStandardMultiple.value) {
		return true
	}
	return isSearchActive.value
})

const showSearchToggle = computed(() =>
	isStandardMultiple.value && isInlineSearch.value && isOpen.value
)

// Получение всех выбираемых элементов
const selectableItems = computed(() => {
	const result: { id: string | number; label: string }[] = []

	props.items.forEach(item => {
		if (item.type === 'group') {
			item.children.forEach(child => {
				if (!child.disabled) {
					result.push({ id: child.id, label: child.label })
				}
			})
		} else {
			if (!item.disabled) {
				result.push({ id: item.id, label: item.label })
			}
		}
	})
	return result
})

// Поиск оригинальной группы для получения полного количества детей
const findOriginalGroup = (groupId: string | number) => {
	return props.items.find(item =>
		item.type === 'group' && item.id === groupId
	) as SelectGroup | undefined
}

// Совпадение строки с активным поисковым запросом. Пустой запрос ничего не «находит» —
// на этом держится режим «Показать выбранных» без фильтрации
const matchesSearch = (label: string): boolean => {
	if (!searchQuery.value) {
		return false
	}

	return label.toLowerCase().includes(searchQuery.value.toLowerCase())
}

// Фильтрация при поиске
const processedItems = computed((): SelectOption[] => {
	if (!props.searchable || !searchQuery.value) {
		return props.items.map(item => {
			if (item.type === 'group') {
				return {
					...item,
					showChildren: true
				}
			}
			return item
		})
	}

	return props.items.reduce<SelectOption[]>((acc, item) => {
		if (item.type === 'group') {
			const groupMatches = item.label.toLowerCase().includes(searchQuery.value.toLowerCase())

			if (groupMatches) {
				acc.push({
					...item,
					showChildren: true
				})
			} else {
				const matchingChildren = item.children.filter(child =>
					child.label.toLowerCase().includes(searchQuery.value.toLowerCase())
				)
				if (matchingChildren.length > 0) {
					acc.push({
						...item,
						children: matchingChildren,
						showChildren: true
					})
				}
			}
		} else {
			if (item.label.toLowerCase().includes(searchQuery.value.toLowerCase())) {
				acc.push(item)
			}
		}

		return acc
	}, [])
})

// Выбранные значения
const selectedValue = computed(() => {
	if (props.multiple) return null
	return props.modelValue
})

const selectedValues = computed((): (string | number)[] => {
	if (!props.multiple) return []
	return Array.isArray(props.modelValue) ? props.modelValue : []
})

const isSelectedOnlyMode = computed(() => showOnlySelected.value && props.multiple)

// «Показать выбранных» показывает всех выбранных независимо от поиска плюс всё, что подходит
// под текущий запрос, поэтому считаем от исходного списка, а не от уже отфильтрованного
const selectedOrMatchedItems = computed((): SelectOption[] => {
	return props.items.reduce<SelectOption[]>((acc, item) => {
		if (item.type === 'group') {
			const children = matchesSearch(item.label)
				? item.children
				: item.children.filter(child =>
					selectedValues.value.includes(child.id) || matchesSearch(child.label)
				)

			// Группы без участников выбираются собственным id — их тоже нельзя терять
			if (children.length > 0 || selectedValues.value.includes(item.id) || matchesSearch(item.label)) {
				acc.push({
					...item,
					children,
					showChildren: true
				})
			}
		} else {
			if (selectedValues.value.includes(item.id) || matchesSearch(item.label)) {
				acc.push(item)
			}
		}

		return acc
	}, [])
})

// Фильтрация по выбранным элементам
const filteredItemsWithSelected = computed((): SelectOption[] => {
	if (!isSelectedOnlyMode.value) {
		return processedItems.value
	}

	return selectedOrMatchedItems.value
})

// Плоский список того, что реально отрисовано — по нему ходят стрелки и Enter
const filteredSelectableItems = computed(() => {
	const result: { id: string | number; label: string }[] = []

	filteredItemsWithSelected.value.forEach(item => {
		if (item.type === 'group') {
			if (!item.showChildren) {
				return
			}

			item.children.forEach((child: SelectItem) => {
				if (!child.disabled) {
					result.push({ id: child.id, label: child.label })
				}
			})
		} else {
			if (!item.disabled) {
				result.push({ id: item.id, label: item.label })
			}
		}
	})

	return result
})

// Закреплённые строки над списком
const visiblePinnedItems = computed((): SelectOption[] => {
	if (!props.pinnedItems.length) {
		return []
	}

	if (isSelectedOnlyMode.value) {
		return props.pinnedItems.filter(item =>
			selectedValues.value.includes(item.id) || matchesSearch(item.label)
		)
	}

	if (searchQuery.value) {
		return []
	}

	return props.pinnedItems
})

// Количество выбранных элементов
const selectedValuesLength = computed(() => {
	if (props.multiple) {
		return selectedValues.value.length
	}
	return selectedValue.value != null ? 1 : 0
})

const showClearIcon = computed(() => {
	if (!isStandardMultiple.value) {
		return props.clearable && selectedValuesLength.value > 0
	}

	return isSearchFieldActive.value && searchQuery.value.length > 0
})

const editContainerStyle = computed(() =>
	isStandardMultiple.value && isOpen.value ? { right: '80px' } : {}
)

const resolvedPlaceholder = computed(() =>
	props.placeholder || String(i18n.global.t('ui_select_placeholder'))
)
const resolvedSearchPlaceholder = computed(() =>
	props.searchPlaceholder || String(i18n.global.t('search_label'))
)
const triggerPlaceholder = computed(() => (props.floatingLabel ? '' : resolvedPlaceholder.value))

const isLabelActive = computed(() => isOpen.value || isFocused.value || selectedValuesLength.value > 0)

// Состояние группы
const getGroupState = (group: SelectGroup) => {
	const childIds = group.children.map(child => child.id)
	const hasDisabledChildren = group.children.some(child => child.disabled)

	return {
		isSelected: childIds.length > 0 && childIds.every(id => selectedValues.value.includes(id)),
		isDisabled: group.disabled || hasDisabledChildren
	}
}

// В нашем списке группа приходит уже отфильтрованным клоном, поэтому состояние считаем
// по исходному составу
const getOwnGroupState = (group: SelectOption) => {
	return getGroupState((findOriginalGroup(group.id) ?? group) as SelectGroup)
}

// Проверка выбран ли элемент
const isSelected = (value: string | number): boolean => {
	if (props.multiple) {
		return selectedValues.value.includes(value)
	}
	return selectedValue.value === value
}

const getDescription = (item: SelectItem): string => {
	if (!props.descriptionKey) {
		return ''
	}

	const value = item[props.descriptionKey]
	return value == null ? '' : String(value)
}

const getDescriptionTooltip = (item: SelectItem): string => escapeHtml(getDescription(item))

// Получение отображаемого текста
const getDisplayValue = (value: string | number): string => {
	const label = findDisplayItem(props.items, value) ?? findDisplayItem(props.pinnedItems, value)
	return label || String(value)
}

const findDisplayItem = (items: SelectOption[], value: string | number): string | undefined => {
	for (const item of items) {
		if (item.type === 'group') {
			if (item.id === value) return item.label
			const found = item.children.find(child => child.id === value)
			if (found) return found.label
		} else {
			if (item.id === value) return item.label
		}
	}
	return undefined
}

// Элементы для отображения в триггере
const selectedDisplayItems = computed(() => {
	return selectedValues.value.map(id => ({
		id,
		label: getDisplayValue(id)
	}))
})

// Сводка по количеству выбранного для триггера стандартного мультиселекта.
// Единственный выбранный элемент показываем полностью
const selectedCountLabel = computed(() => {
	const count = selectedValues.value.length

	if (count === 1) {
		return getDisplayValue(selectedValues.value[0])
	}

	const noun = props.selectedCountKey ? ` ${tp(props.selectedCountKey, count)}` : ''

	return `${String(i18n.global.t('selected_label'))} ${count}${noun}`
})

// Проверка выбраны ли все
const isAllSelected = computed((): boolean => {
	if (!props.multiple) return false
	return selectableItems.value.length > 0 &&
		selectableItems.value.every(item => selectedValues.value.includes(item.id))
})

// Обновление позиции дропдауна
const updateDropdownPosition = () => {
	if (!triggerRef.value || !isOpen.value) return

	const rect = triggerRef.value.getBoundingClientRect()
	const viewportHeight = window.innerHeight
	const spaceBelow = viewportHeight - rect.bottom
	const spaceAbove = rect.top

	// Определяем направление открытия
	let openUp = false
	if (props.dropdownHeight === undefined) {
		// При динамической высоте используем старую логику
		openUp = spaceBelow < 300 && spaceAbove > spaceBelow
	} else {
		// При фиксированной высоте тоже можно определять направление,
		// чтобы дропдаун не выходил за экран по вертикали
		openUp = spaceBelow < (typeof props.dropdownHeight === 'number' ? props.dropdownHeight : parseFloat(props.dropdownHeight)) && spaceAbove > spaceBelow
	}

	// Вычисляем ширину (без изменений)
	let width = `${rect.width}px`
	if (props.minDropdownWidth) {
		const minWidth = typeof props.minDropdownWidth === 'number'
			? `${props.minDropdownWidth}px`
			: props.minDropdownWidth
		width = `max(${minWidth}, ${rect.width}px)`
	}

	// Базовые стили позиционирования
	const styles: any = {
		position: 'fixed',
		top: openUp ? 'auto' : `${rect.bottom + 4}px`,
		bottom: openUp ? `${viewportHeight - rect.top + 4}px` : 'auto',
		left: `${rect.left}px`,
		width: width,
		minWidth: props.minDropdownWidth ? `${rect.width}px` : 'auto',
		zIndex: 10001
	}

	if (props.dropdownHeight !== undefined) {
		// Фиксированная высота
		const heightValue = typeof props.dropdownHeight === 'number'
			? `${props.dropdownHeight}px`
			: props.dropdownHeight
		styles.height = heightValue
		// Убираем maxHeight, чтобы не конфликтовать
		styles.maxHeight = 'none'
	} else {
		// Динамическая высота
		const availableHeight = openUp ? spaceAbove - 20 : spaceBelow - 20
		let maxH = Math.max(100, availableHeight)
		if (props.maxDropdownHeight !== undefined) {
			const limit = typeof props.maxDropdownHeight === 'number'
				? props.maxDropdownHeight
				: parseFloat(props.maxDropdownHeight)
			if (!Number.isNaN(limit)) {
				maxH = Math.min(maxH, limit)
			}
		}
		styles.maxHeight = `${maxH}px`
	}

	dropdownStyles.value = styles
}

// const minTriggerHeight =computed(() => {
// 	const styles: any = {
// 		minHeight:`32px`,
// 	}

// 	if (props.minTriggerHeight !== 32) {
// 		styles.minHeight = `${typeof props.minTriggerHeight === 'number'
// 			? props.minTriggerHeight
// 			: parseFloat(props.minTriggerHeight)}px`
// 	}

// 	return styles;
// });

// Сброс выбора
const resetSelection = (): void => {
	if (!props.multiple) return

	emit('update:modelValue', [])
	emit('select', [])
}

const toggleShowOnlySelected = (value: boolean) => {
	showOnlySelected.value = value
}

// Очистка значения
const handleClear = () => {
	if (props.multiple) {
		emit('update:modelValue', [])
		emit('select', [])
	} else {
		emit('update:modelValue', null)
		emit('select', null)
	}
}

// Возврат каретки в поисковое поле после выбора не мышью: режим поиска гасится только лупой,
// поэтому фокус тоже должен оставаться в инпуте
const restoreSearchFocus = (): void => {
	if (!isStandardMultiple.value || !isSearchFieldActive.value) {
		return
	}

	nextTick(() => editInputRef.value?.focus({ preventScroll: true }))
}

const activateSearch = (): void => {
	isSearchActive.value = true

	nextTick(() => editInputRef.value?.focus())
}

// Клик по лупе переключает режим: активный поиск выключаем и сбрасываем фильтр,
// чтобы в списке снова были все элементы
const toggleSearch = (): void => {
	if (isSearchFieldActive.value) {
		searchQuery.value = ''
		isSearchActive.value = false
		editInputRef.value?.blur()
		return
	}

	activateSearch()
}

// Клик по стрелочке: в режиме поиска чистит введённый текст, в остальных селектах — выбор
const handleClearClick = (): void => {
	if (isSearchFieldActive.value) {
		searchQuery.value = ''
		editInputRef.value?.focus()
		return
	}

	handleClear()
}

// Обработка клика по триггеру
const handleTriggerClick = () => {
	if (props.disabled) return

	// У открытого мультиселекта клик по полю ничего не переключает: режим поиска включает
	// и выключает только лупа, а свернуть список можно шевроном, кликом вне селекта или по Esc
	if (isOpen.value && isStandardMultiple.value && isInlineSearch.value) {
		return
	}

	toggleDropdown()
}

// Обработка Enter на триггере
const handleTriggerEnter = (): void => {
	if (props.disabled) return

	if (!isOpen.value) {
		toggleDropdown()
	} else {
		nextTick(() => {
			if (dropdownRef.value) {
				dropdownRef.value.focus()
			}
		})
	}
}

// Обработка Enter в поиске
const handleSearchEnter = () => {
	if (!isOpen.value) return;

	if (focusedValue.value) {
		const isInList = filteredSelectableItems.value.some(
			item => item.id === focusedValue.value
		);

		if (isInList) {
			handleSelect(focusedValue.value);
			// В мультивыборе Enter только отмечает элемент — дропдаун остаётся открытым
			if (!props.multiple) {
				closeDropdown();
			}
			return;
		}
	}

	const firstItem = filteredSelectableItems.value[0];
	if (firstItem) {
		handleSelect(firstItem.id);
		if (!props.multiple) {
			closeDropdown();
		}
	}
};

// Обработка Enter в дропдауне
const handleDropdownEnter = (): void => {
	if (!isOpen.value || !focusedValue.value) return

	const isInFilteredList = filteredSelectableItems.value.some(
		item => item.id === focusedValue.value
	)

	if (!isInFilteredList) return

	const item = findEnterPressedItem(filteredItemsWithSelected.value)
	if (item && !item.disabled) {
		if (item.type !== 'group') {
			handleSelect(item.id)
		}
	}
}

const findEnterPressedItem = (items: SelectOption[]): SelectItem | undefined => {
	for (const item of items) {
		if (item.type === 'group') {
			if (item.id === focusedValue.value) {
				if (props.multiple && (item as SelectGroup).selectable) {
					toggleGroupSelection(item as SelectGroup)
					return undefined
				}
				return undefined
			}
			const found = item.children.find(child => child.id === focusedValue.value)
			if (found) return found
		} else {
			if (item.id === focusedValue.value) return item
		}
	}
	return undefined
}

// Обработка клавиш на триггере
const handleTriggerKeyDown = (event: KeyboardEvent): void => {
	if (!isOpen.value) {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			toggleDropdown()
		}
	}
}

// Обработка клавиш в дропдауне
const handleDropdownKeyDown = (event: KeyboardEvent): void => {
	if (!isOpen.value || !filteredSelectableItems.value.length) return

	const currentIndex = filteredSelectableItems.value.findIndex(
		item => item.id === focusedValue.value
	)

	let newIndex: number

	switch (event.key) {
		case 'ArrowDown':
			event.preventDefault()
			newIndex = currentIndex < filteredSelectableItems.value.length - 1 ? currentIndex + 1 : 0
			focusedValue.value = filteredSelectableItems.value[newIndex].id
			nextTick(() => scrollToFocused())
			break

		case 'ArrowUp':
			event.preventDefault()
			newIndex = currentIndex > 0 ? currentIndex - 1 : filteredSelectableItems.value.length - 1
			focusedValue.value = filteredSelectableItems.value[newIndex].id
			nextTick(() => scrollToFocused())
			break
	}
}

// Обработка выбора элемента
const handleSelect = (value: string | number, event?: Event): void => {
	if (props.disabled) return

	if (event) {
		event.stopPropagation()
	}

	let newValue: SelectModelValue

	if (props.multiple) {
		const values = [...selectedValues.value]
		const index = values.indexOf(value)

		if (index === -1) {
			values.push(value)
		} else {
			values.splice(index, 1)
		}

		newValue = values
	} else {
		newValue = value
		if (props.closeOnSelect) {
			setTimeout(() => {
				isOpen.value = false
				emit('close')
			}, 100)
		}
	}

	emit('update:modelValue', newValue)
	emit('select', newValue)

	if (props.searchable && !props.multiple) {
		searchQuery.value = ''
	}

	restoreSearchFocus()
}

// Обработка клика по элементу
const handleItemClick = (item: SelectItem): void => {
	if (item.disabled || item.type === 'group') return

	if (!props.multiple) {
		handleSelect(item.id)
	}
}

// Можно ли выбрать группу целиком (при единичном выборе — нельзя)
const isGroupSelectable = (group: SelectOption): boolean => {
	return props.multiple && group.selectable !== false
}

// Наведение на заголовок группы
const handleGroupHeaderEnter = (group: SelectOption): void => {
	focusedValue.value = isGroupSelectable(group)
		? group.id
		: null
}

// Переключение выбора группы
const toggleGroupSelection = (group: SelectGroup): void => {
	if (!props.multiple || group.selectable === false || group.disabled) return

	const originalGroup = findOriginalGroup(group.id)
	if (!originalGroup) return

	const childIds = originalGroup.children
		.filter(child => !child.disabled)
		.map(child => child.id)

	const state = getGroupState(originalGroup)
	const currentValues = [...selectedValues.value]

	let newValues: (string | number)[]

	if (state.isSelected) {
		newValues = currentValues.filter(id => !childIds.includes(id))
	} else {
		newValues = [...currentValues]
		childIds.forEach(id => {
			if (!newValues.includes(id)) {
				newValues.push(id)
			}
		})
	}

	emit('update:modelValue', newValues)
	emit('select', newValues)

	restoreSearchFocus()
}

// Выбрать все
const toggleAll = (): void => {
	if (!props.multiple) return

	if (isAllSelected.value) {
		emit('update:modelValue', [])
		emit('select', [])
	} else {
		emit('update:modelValue', selectableItems.value.map(item => item.id))
		emit('select', selectableItems.value.map(item => item.id))
	}
}

// Переключение дропдауна
const toggleDropdown = (): void => {
	if (props.disabled) return

	isOpen.value = !isOpen.value

	if (isOpen.value) {
		emit('open')
		updateDropdownPosition()

		const firstSelectable = filteredSelectableItems.value[0]
		focusedValue.value = firstSelectable?.id || null

		nextTick(() => {
			if (isInlineSearch.value) {
				editInputRef.value?.focus()
			} else if (props.searchable) {
				$searchInputRef.value?.focusInput()
			} else {
				dropdownRef.value?.focus()
			}
		})
	} else {
		emit('close')
		if (props.multiple) {
			searchQuery.value = ''
		}
	}
}

// Закрытие дропдауна
const closeDropdown = (): void => {
	if (isOpen.value) {
		isOpen.value = false
		emit('close')
		if (!props.multiple) {
			searchQuery.value = ''
		}
		// triggerRef.value?.focus()
		triggerRef.value?.blur()
	}
}

// Скролл к фокусированному элементу
const scrollToFocused = (): void => {
	if (!dropdownRef.value || !focusedValue.value) return

	const focusedElement = dropdownRef.value.querySelector(
		'.app-select__option--focused, .app-select__group-header--focused'
	) as HTMLElement

	if (focusedElement) {
		focusedElement.scrollIntoView({
			block: 'center',
			behavior: 'smooth'
		})
	}
}

// Обработка Escape
const handleEscape = (event: KeyboardEvent) => {
	if (event.key === 'Escape') {
		event.preventDefault()
		event.stopPropagation()
		if (isOpen.value) {
			closeDropdown()
		}
	}
}

watch(searchQuery, (value) => {
	emit('search', value)
	const firstSelectable = filteredSelectableItems.value[0]
	focusedValue.value = firstSelectable?.id || null
})

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as Node
  if (
    triggerRef.value?.contains(target) ||
    dropdownRef.value?.contains(target)
  ) return
  closeDropdown()
}

watch(isOpen, (newVal) => {
	if (newVal) {
		isSearchActive.value = isInlineSearch.value
		// document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape, { capture: true })
		window.addEventListener('scroll', updateDropdownPosition, true)
		window.addEventListener('resize', updateDropdownPosition)
	} else {
		isSearchActive.value = false
		// document.removeEventListener('mousedown', handleClickOutside)
		document.removeEventListener('keydown', handleEscape, { capture: true })
		window.removeEventListener('scroll', updateDropdownPosition, true)
		window.removeEventListener('resize', updateDropdownPosition)
		showOnlySelected.value = false
		if (props.multiple) {
			searchQuery.value = ''
		}
	}
})

const open = (): void => {
	if (!isOpen.value) {
		toggleDropdown()
	}
}

defineExpose({ open, close: closeDropdown, focusSearch: activateSearch })

onUnmounted(() => {
	document.removeEventListener('keydown', handleEscape, { capture: true })
	window.removeEventListener('scroll', updateDropdownPosition, true)
	window.removeEventListener('resize', updateDropdownPosition)
})
</script>

<style lang="scss">
@import "@/styles/color.scss";

.app-select {
	position: relative;
	width: 100%;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	font-size: 14px;

	&__trigger {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 5px 12px;
		border: 1px solid #D2D2D2;
		border-radius: 8px;
		background-color: #FFF;
		cursor: pointer;
		min-height: 32px;
		transition: border-color 0.2s ease;

		&:hover {
			border-color: #3F884F;
		}

		&:focus {
			outline: none;
			border-color: #3F884F;
		}

		&--editable {
			cursor: text;
			padding: 4px 12px;
		}
	}

	&--searchable &__trigger {
		cursor: text;
	}

	&--open &__trigger {
		border-color: #3F884F;
	}

	&--focused &__trigger {
		border-color: #3F884F;
	}

	&--disabled &__trigger {
		background-color: #f5f5f5;
		cursor: not-allowed;
		color: rgba(0, 0, 0, 0.25);
		border-color: #D2D2D2;

		&:hover {
			border-color: #D2D2D2;
		}
	}

	&__values {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 4px;
		min-height: 20px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		transition: opacity 0.2s ease;

		&--hidden {
			opacity: 0;
			pointer-events: none;
		}
	}

	&__floating-label {
		position: absolute;
		left: 12px;
		top: 50%;
		transform: translateY(-50%);
		color: #9C9C9C;
		font-size: 14px;
		pointer-events: none;
		transition: all 0.3s ease;
		background: #FFF;
		padding: 0 4px;
		z-index: 1;

		&.active {
			top: 0;
			font-size: 10px;
			color: #3F884F;
		}
	}

	&__floating-label-required {
		color: #FF5533;
		margin-left: 0.2em;
	}

	&__first-value {
		font-size: 14px;
		line-height: 17px;
		font-weight: 400;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: $color-text_main;
	}

	&__additional-count {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0 3px;
		background-color: rgba(#3F884F, 0.2);
		color: #333;
		border-radius: 4px;
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		margin-left: 4px;
	}

	&__single-value {
		font-size: 14px;
		line-height: 18px;
		color: $color-text_main;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__placeholder {
		color: #D2D2D2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__edit-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 40px;
		bottom: 0;
		display: flex;
		align-items: center;
		padding: 0 12px;
		background: transparent;
		z-index: 2;

		&--hidden {
			visibility: hidden;
		}
	}

	&__edit-input {
		width: 100%;
		border: none;
		outline: none;
		font-size: 14px;
		line-height: 18px;
		color: #333;
		background: transparent;
		padding: 0;

		&::placeholder {
			color: #D2D2D2;
		}
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 4px;
		margin-left: 8px;
		position: relative;
		z-index: 3;
	}

	&__clear-button {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: none;
		background: none;
		cursor: pointer;
		opacity: 0.6;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 1;
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.3;
		}
	}

	&__arrow {
		transition: transform 0.2s ease;
		cursor: pointer;
	}

	&--open &__arrow {
		transform: rotate(180deg);
	}

	&__dropdown {
		display: flex;
		flex-direction: column;
		background-color: #FFF;
		border-radius: 4px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		overflow: hidden;

		&:focus {
			outline: none;
		}

		&--default {
			padding: 12px;
			box-sizing: border-box;

			.app-select__search {
				padding: 0 0 8px;
			}

			.app-select__controls {
				padding: 0 0 2px;
			}
		}

		&--single {
			.app-select__option--selected {
				color: #499259;
			}

			.app-select__option:hover,
			.app-select__option--focused {
				background-color: #F2F2F2;
			}

			.app-select__option--disabled:hover {
				background-color: transparent;
			}
		}
	}

	&__dropdown-close {
		position: absolute;
		top: 12px;
		right: 12px;
		z-index: 2;
	}

	&__search {
		padding: 12px 12px 8px;
		background-color: #FFF;
	}

	&__search-input {
		width: 100%;
	}

	&__controls {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 12px 8px;
		background-color: #FFF;

		&-left {
			display: flex;
			align-items: center;
		}

		&-text {
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
			margin-left: 8px;
		}

		&-right {
			display: flex;
			align-items: center;
		}
	}

	&__reset-button {
		background: none;
		border: none;
		color: $color-text-secondary;
		font-size: 14px;
		cursor: pointer;
		padding: 4px 8px;

		&:hover {
			color: $color-text-secondary;
			text-decoration: underline;
		}

		&:disabled {
			color: #D2D2D2;
			cursor: not-allowed;
			text-decoration: none;
		}
	}

	&__options-container {
		// overflow-y: auto;
		// max-height: 450px;
		overflow-y: auto;
		flex: 1;
	}

	&__divider {
		flex: 0 0 1px;
		width: 100%;
		background-color: $color-divider;
		margin-bottom: 8px;
		margin-inline: 12px;
		width: 673px;
	}

	&__options {
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	&__group {
		display: flex;
		flex-direction: column;
		gap: 2px;

		&-header {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 4px 16px 4px 0;
			border-radius: 8px;
			font-weight: 500;
			cursor: default;

			&--selectable {
				cursor: pointer;
			}

			&--static {
				cursor: default;
				user-select: none;

				&:hover {
					background-color: transparent;
				}
			}

			&--disabled {
				color: rgba(0, 0, 0, 0.25);
				background-color: #f5f5f5;
				cursor: not-allowed;
			}
		}

		&-label {
			display: flex;
			align-items: center;
			gap: 6px;
			flex: 1;
			min-width: 0;
		}

		&-title {
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			font-weight: 500;
		}

		&-count {
			flex-shrink: 0;
			background-color: #F2F2F2;
			color: #979797;
			font-size: 12px;
			line-height: 14px;
			font-weight: 400;
			padding: 2px 6px;
			border-radius: 8px;
		}

		&-children {
			display: flex;
			flex-direction: column;
			gap: 2px;
		}
	}

	&__option {
		padding: 4px 16px;
		border-radius: 8px;
		cursor: pointer;
		color: #4D4D4D;
		transition: background-color 0.2s ease;

		&--disabled {
			color: rgba(0, 0, 0, 0.25);
			cursor: not-allowed;
		}

		&--select-all {
			padding-left: 0;
		}

		&--child {
			padding-left: 24px;
		}

		&--pinned {
			.app-select__dropdown:not(.app-select__dropdown--single) & {
				padding-left: 0;
			}
		}

		&--with-append {
			position: relative;
			padding-right: 40px;
		}

		&-label {
			display: flex;
			align-items: flex-start;
			gap: 8px;
			cursor: pointer;
			width: 100%;
			user-select: none;
		}

		&-content {
			display: flex;
			flex-direction: column;
			flex: 1;
			min-width: 0;
		}

		&-name {
			font-size: 14px;
			line-height: 18px;
		}

		&-label &-name {
			overflow-wrap: anywhere;
			word-break: break-word;
		}

		&-description {
			display: block;
			font-size: 12px;
			line-height: 16px;
			color: #979797;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&__option-append {
		position: absolute;
		top: 0;
		bottom: 0;
		right: 16px;
		display: flex;
		align-items: center;
	}

	&__option-tooltip {
		min-width: 0;

		&.app-tooltip > .app-tooltip__activator {
			display: block;
			min-width: 0;
		}
	}

	&__empty {
		padding: 12px;
		text-align: center;
		color: #999;
	}

	&-dropdown-enter-active,
	&-dropdown-leave-active {
		transition: opacity 0.2s ease, transform 0.2s ease;
	}

	&-dropdown-enter-from,
	&-dropdown-leave-to {
		opacity: 0;
		transform: translateY(-8px);
	}
}

@media (max-width: 480px) {
	.select-dropdown--choice-rules-modal {
		top: 0 !important;
		right: 0 !important;
		bottom: auto !important;
		left: 0 !important;
		width: 100vw !important;
		min-width: 0 !important;
		height: 100vh !important;
		height: 100dvh !important;
		max-height: 100vh !important;
		max-height: 100dvh !important;
		margin-left: 0 !important;
		border-radius: 0 !important;
		box-sizing: border-box;
		overflow-x: hidden;

		.app-select__dropdown-header {
			width: 100% !important;
			box-sizing: border-box;
		}

		.app-select__search {
			padding-right: 44px;
		}

		.app-select__divider {
			width: calc(100% - 24px);
		}

		.app-select__options-container {
			overflow-x: hidden;
		}
	}
}
</style>
